import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Cluster } from '../hooks/useClusters';
import { cn } from '../../../ui/utils/cn';

interface ClusterListProps {
    clusters: Cluster[];
    loading: boolean;
    error?: string | null;
    onRetry?: () => void;
    selectedId: string | null;
    onSelect: (cluster: Cluster) => void;
    onFetch: (id: string) => void;
    onRefresh: (id: string) => void;
    onSuggestSplit: (id: string) => void;
    fetchingId: string | null;
}

const ClusterList: React.FC<ClusterListProps> = ({
    clusters,
    loading,
    error,
    onRetry,
    selectedId,
    onSelect,
    onFetch,
    onRefresh,
    onSuggestSplit,
    fetchingId
}) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8 text-sm space-y-2">
                <p className="text-destructive flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {error}
                </p>
                {onRetry && (
                    <button
                        type="button"
                        onClick={onRetry}
                        className="text-primary hover:underline"
                    >
                        Retry
                    </button>
                )}
            </div>
        );
    }

    if (clusters.length === 0) {
        return (
            <div className="text-center py-8 text-muted-foreground text-sm">
                No clusters yet. Create one by selecting cells on the map.
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {clusters.map((cluster) => {
                const isSelected = selectedId === cluster._id;
                const isFetching = fetchingId === cluster._id;
                const isStale = cluster.nextRefreshAfter && new Date(cluster.nextRefreshAfter) < new Date();

                return (
                    <div
                        key={cluster._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelect(cluster)}
                        onKeyDown={(e) => e.key === 'Enter' && onSelect(cluster)}
                        className={cn(
                            'p-3 rounded-lg border border-ink/15 cursor-pointer transition-all',
                            isSelected ? 'bg-mint/10 border-mint/40' : 'hover:bg-muted/30'
                        )}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-sm truncate">{cluster.name}</div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <FontAwesomeIcon icon={faStore} className="h-2.5 w-2.5" />
                                        {cluster.totalShops} shops
                                    </span>
                                    <span>·</span>
                                    <span>{cluster.status}</span>
                                    {cluster.needsSplit && (
                                        <span className="text-amber-600 flex items-center gap-2">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="h-2.5 w-2.5" />
                                            needs split
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                            {cluster.status === 'draft' && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onFetch(cluster._id); }}
                                    disabled={isFetching}
                                    className="text-xs px-2 py-1 rounded bg-mint/20 text-mint hover:bg-mint/30 disabled:opacity-50"
                                >
                                    {isFetching ? 'Fetching...' : 'Fetch shops'}
                                </button>
                            )}
                            {isStale && cluster.status === 'active' && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onRefresh(cluster._id); }}
                                    disabled={isFetching}
                                    className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                                >
                                    Refresh
                                </button>
                            )}
                            {cluster.needsSplit && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onSuggestSplit(cluster._id); }}
                                    className="text-xs px-2 py-1 rounded bg-muted/50 hover:bg-muted"
                                >
                                    Suggest split
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ClusterList;
